import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Users, Plus, Edit2, Trash2, Loader2, Network, TrendingUp, FileText, Megaphone, Clock, Sparkles, ExternalLink, CheckCircle2, GripVertical, PlusCircle, ChevronRight as ChevronRightIcon, Settings as SettingsIcon, DollarSign, Heart, Building2, Save, X as XIcon, Globe, Mail, Phone, Inbox } from 'lucide-react';
import logger from '../utils/logger';
import { LEVELS, LL, LC, SL, EMP_TYPE_LABEL, EMP_TYPE_STYLES, STATUS_STYLES, POSTING_STATUSES, JD_STATUSES } from './staffing/constants';
import { Section, PosDialog, JdDialog, PostDialog, AppsDialog } from './staffing/dialogs';
import SettingsTab from './staffing/SettingsTab';
import ExternalApplicantsTab from './staffing/ExternalApplicantsTab';

export default function StaffingPage() {
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'administrator';
  const [tab, setTab] = useState('positions');
  const [positions, setPositions] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [projections, setProjections] = useState(null);
  const [jds, setJds] = useState([]);
  const [postings, setPostings] = useState([]);
  const [config, setConfig] = useState({ salary_bands: [], benefits: [], divisions: [] });
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [apps, setApps] = useState([]);
  const [externalApps, setExternalApps] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a,b,c,d,e,f] = await Promise.all([api.get('/staffing/positions'),api.get('/staffing/hierarchy'),api.get('/staffing/projections'),api.get('/staffing/job-descriptions'),api.get('/staffing/postings'),api.get('/staffing/config')]);
      setPositions(a.data);setHierarchy(b.data);setProjections(c.data);setJds(d.data);setPostings(e.data);setConfig(f.data);
      if (user?.role === 'administrator' || user?.role === 'supervisor') {
        try { const { data: ext } = await api.get('/staffing/external-applications'); setExternalApps(ext); } catch {}
      }
    } catch(err){logger.error(err);}
    setLoading(false);
  }, [api, user]);

  useEffect(() => {fetchAll();}, [fetchAll]);

  const save = async (type) => {
    setSaving(true);
    try {
      const id = form.id;
      const paths = {position:'positions',jd:'job-descriptions',posting:'postings'};
      if(id) await api.put(`/staffing/${paths[type]}/${id}`, form);
      else await api.post(`/staffing/${paths[type]}`, form);
      setDlg(null);toast.success('Saved');fetchAll();
    } catch(err){toast.error(err.response?.data?.detail||'Error');}
    setSaving(false);
  };

  const del = async (type, id) => {
    if(!window.confirm('Delete?')) return;
    const paths = {position:'positions',jd:'job-descriptions',posting:'postings'};
    await api.delete(`/staffing/${paths[type]}/${id}`);fetchAll();
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const useTitle = form.gen_title || form.title || '';
      const {data} = await api.post('/staffing/job-descriptions/generate',{title:useTitle,duties:form.gen_duties||'',level:form.gen_level||'',service_line:form.service_line||''});
      setForm(p=>({...p, title: p.title || useTitle, summary:data.summary||p.summary||'', duties:data.duties||p.duties||'', qualifications:data.qualifications||p.qualifications||'', physical_reqs:data.physical_reqs||p.physical_reqs||'', benefits:data.benefits||p.benefits||'', certifications:data.certifications||p.certifications||'', salary_range:data.salary_range||p.salary_range||'', content:data.content||p.content||''}));
      toast.success('All sections filled!');
    } catch{toast.error('Generation failed');}
    setGenerating(false);
  };

  const openApps = async (p) => {
    try{const{data}=await api.get(`/staffing/postings/${p.id}/applications`);setApps(data);}catch{setApps([]);}
    setForm({...p,_t:'apps'});setDlg('apps');
  };

  const updateApp = async (id, status) => {
    await api.put(`/staffing/applications/${id}/status`,{status});
    if(form.id){const{data}=await api.get(`/staffing/postings/${form.id}/applications`);setApps(data);}
  };

  const renderHier = (nodes, depth=0, opts={}) => {
    const { dragId, setDragId, hoverId, setHoverId, handleDrop, isAdmin, onAddChild, onEdit, onDelete } = opts;
    return nodes.map(n=>{
      const isHovered = hoverId === n.id;
      const isDragging = dragId === n.id;
      return (
        <div key={n.id} className="mb-1 relative" style={{marginLeft:depth*24}} data-testid={`hier-node-wrap-${n.id}`}>
          {depth>0 && <span className="absolute left-[-12px] top-[18px] w-3 h-px bg-[#DED8EB]" aria-hidden="true" />}
          <div
            draggable={isAdmin&&!!setDragId}
            onDragStart={(e)=>{setDragId&&setDragId(n.id); e.dataTransfer.effectAllowed='move';}}
            onDragEnd={()=>{setDragId&&setDragId(null); setHoverId&&setHoverId(null);}}
            onDragOver={e=>{if(handleDrop){e.preventDefault(); setHoverId&&setHoverId(n.id);}}}
            onDragLeave={()=>setHoverId&&setHoverId(null)}
            onDrop={()=>{handleDrop&&handleDrop(n.id); setHoverId&&setHoverId(null);}}
            className={`group flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white border transition-all ${
              isHovered ? 'border-[#6B3FA0] bg-[#EDE7F6] ring-2 ring-[#6B3FA0]/20' :
              isDragging ? 'border-[#9B7ED8] opacity-40' :
              'border-[#DED8EB]'
            }`}
            data-testid={`hier-node-${n.id}`}
          >
            {isAdmin && setDragId && (
              <GripVertical className="h-4 w-4 text-[#C4BDD2] shrink-0 cursor-grab group-hover:text-[#6B3FA0]" data-testid={`drag-handle-${n.id}`} />
            )}
            <Badge className={`text-[10px] shrink-0 ${LC[n.level]||''}`}>{LL[n.level]}</Badge>
            <span className="text-sm font-semibold text-[#2D1B4E] truncate">{n.title}</span>
            {n.service_line && <Badge className="text-[9px] bg-[#F0EDF6] text-[#5A4D6E] shrink-0">{SL[n.service_line]}</Badge>}
            <span className={`text-xs font-mono ml-auto shrink-0 ${n.filled<n.budgeted?'text-[#E65100] font-bold':'text-[#2E7D32]'}`} title="Filled / Budgeted">
              {n.filled}/{n.budgeted}
            </span>
            {isAdmin && onAddChild && (
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <Button variant="ghost" size="icon" onClick={()=>onAddChild(n.id)} className="h-6 w-6 text-[#8E85A0] hover:text-[#2E7D32] hover:bg-[#E8F5E9]" title="Add direct report" data-testid={`add-child-${n.id}`}>
                  <PlusCircle className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" onClick={()=>onEdit(n)} className="h-6 w-6 text-[#8E85A0] hover:text-[#6B3FA0]" title="Edit" data-testid={`hier-edit-${n.id}`}>
                  <Edit2 className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" onClick={()=>onDelete(n.id)} className="h-6 w-6 text-[#8E85A0] hover:text-[#D9534F]" title="Delete" data-testid={`hier-delete-${n.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
          {n.children&&n.children.length>0&&renderHier(n.children,depth+1,opts)}
        </div>
      );
    });
  };

  if(loading) return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#6B3FA0]" /></div>;

  return (
    <div className="p-6 sm:p-8 space-y-5" data-testid="staffing-page">
      <div><h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-[#2D1B4E]" style={{fontFamily:'Manrope,sans-serif'}}>Staffing</h1><p className="text-sm text-[#8E85A0] mt-1">Positions, projections, job descriptions & postings</p></div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F0EDF6] border border-[#DED8EB]" data-testid="staffing-tabs">
          <TabsTrigger value="positions" className="data-[state=active]:bg-white gap-1.5"><Network className="h-3.5 w-3.5"/>Positions</TabsTrigger>
          <TabsTrigger value="projections" className="data-[state=active]:bg-white gap-1.5"><TrendingUp className="h-3.5 w-3.5"/>Projections</TabsTrigger>
          <TabsTrigger value="descriptions" className="data-[state=active]:bg-white gap-1.5"><FileText className="h-3.5 w-3.5"/>Job Descriptions</TabsTrigger>
          <TabsTrigger value="postings" className="data-[state=active]:bg-white gap-1.5"><Megaphone className="h-3.5 w-3.5"/>Postings</TabsTrigger>
          <TabsTrigger value="external" className="data-[state=active]:bg-white gap-1.5" data-testid="tab-external-apps"><Inbox className="h-3.5 w-3.5"/>External Applicants{externalApps.length>0 && <span className="ml-1 bg-[#6B3FA0] text-white rounded-full px-1.5 py-0 text-[9px] font-bold leading-tight">{externalApps.length}</span>}</TabsTrigger>
          {isAdmin && <TabsTrigger value="settings" className="data-[state=active]:bg-white gap-1.5"><SettingsIcon className="h-3.5 w-3.5"/>Settings</TabsTrigger>}
        </TabsList>
        {tab==='positions'&&<PositionsTab positions={positions} hierarchy={hierarchy} isAdmin={isAdmin} renderHier={renderHier} setForm={setForm} setDlg={setDlg} del={del} api={api} fetchAll={fetchAll} config={config}/>}
        {tab==='projections'&&projections&&<ProjectionsTab projections={projections} positions={positions} config={config}/>}
        {tab==='descriptions'&&<DescriptionsTab jds={jds} postings={postings} isAdmin={isAdmin} setForm={setForm} setDlg={setDlg} del={del} config={config} api={api} fetchAll={fetchAll} createPostingFromJd={(jd)=>{
          const desc=[jd.summary,jd.duties].filter(Boolean).join('\n\n');
          setForm({title:jd.title||'',description:desc,requirements:jd.qualifications||'',service_line:jd.service_line||'',deadline:'',salary_range:jd.salary_range||'',job_description_id:jd.id,status:'open'});
          setDlg('posting');
        }}/>}
        {tab==='postings'&&<PostingsTab postings={postings} jds={jds} isAdmin={isAdmin} setForm={setForm} setDlg={setDlg} del={del} openApps={openApps}/>}
        {tab==='external'&&<ExternalApplicantsTab apps={externalApps} postings={postings} api={api} refresh={fetchAll}/>}
        {tab==='settings'&&isAdmin&&<SettingsTab config={config} setConfig={setConfig} api={api}/>}
      </Tabs>
      {dlg==='position'&&<PosDialog form={form} setForm={setForm} close={()=>setDlg(null)} save={()=>save('position')} saving={saving} positions={positions} config={config}/>}
      {dlg==='jd'&&<JdDialog form={form} setForm={setForm} close={()=>setDlg(null)} save={()=>save('jd')} saving={saving} generating={generating} generate={generate} config={config}/>}
      {dlg==='posting'&&<PostDialog form={form} setForm={setForm} close={()=>setDlg(null)} save={()=>save('posting')} saving={saving} jds={jds}/>}
      {dlg==='apps'&&<AppsDialog apps={apps} close={()=>setDlg(null)} updateApp={updateApp}/>}
    </div>
  );
}

function PositionsTab({positions,hierarchy,isAdmin,renderHier,setForm,setDlg,del,api,fetchAll,config}){
  const [view, setView] = React.useState('tree');
  const [groupBy, setGroupBy] = React.useState('none');
  const [dragId, setDragId] = React.useState(null);
  const [hoverId, setHoverId] = React.useState(null);
  const [rootHover, setRootHover] = React.useState(false);

  const groupedHierarchy = React.useMemo(() => {
    const SERVICE_GROUPS_LOCAL = [
      {id: 'day_program', label: 'Explore Program'},
      {id: 'employment', label: 'Employment'},
      {id: 'in_home', label: 'IHCS'},
    ];
    if (groupBy === 'none') return null;
    const divs = config?.divisions || [];
    const groups = groupBy === 'division' ? divs : SERVICE_GROUPS_LOCAL;
    return groups.map(g => {
      const key = groupBy === 'division' ? 'division_id' : 'service_line';
      const belongs = (node) => node[key] === g.id || (node.children && node.children.some(belongs));
      const filter = (nodes) => nodes.filter(belongs).map(n => ({
        ...n,
        children: n.children ? filter(n.children) : []
      }));
      return { ...g, tree: filter(hierarchy) };
    }).filter(g => g.tree.length > 0);
  }, [groupBy, hierarchy, config]);

  const handleDrop = async (targetId) => {
    if(!dragId || dragId === targetId) { setDragId(null); return; }
    try { await api.put(`/staffing/positions/${dragId}/move`, {reports_to: targetId||''}); toast.success(targetId?'Reports-to updated':'Moved to top level'); fetchAll(); }
    catch { toast.error('Move failed'); }
    setDragId(null);
  };

  const onAddChild = (parentId) => {
    setForm({title:'',level:'dsp',service_line:'',budgeted:1,filled:0,reports_to:parentId,description:''});
    setDlg('position');
  };
  const onEdit = (p) => { setForm(p); setDlg('position'); };
  const onDelete = (id) => del('position', id);

  const opts = {dragId, setDragId, hoverId, setHoverId, handleDrop, isAdmin, onAddChild, onEdit, onDelete};

  return(<div className="mt-5 space-y-5">
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm text-[#5A4D6E]">{positions.length} positions</p>
        <div className="flex items-center bg-[#F0EDF6] border border-[#DED8EB] rounded-lg p-0.5 ml-3">
          {[['tree','Tree'],['card','Cards'],['table','Table']].map(([k,l])=>(
            <button key={k} onClick={()=>setView(k)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${view===k?'bg-white text-[#2D1B4E] shadow-sm':'text-[#8E85A0]'}`} data-testid={`view-${k}`}>{l}</button>
          ))}
        </div>
        {view==='tree' && (
          <div className="flex items-center gap-1 ml-2">
            <span className="text-[10px] text-[#8E85A0] uppercase tracking-wider">Group by:</span>
            <div className="flex items-center bg-[#F0EDF6] border border-[#DED8EB] rounded-lg p-0.5">
              {[['none','None'],['division','Division'],['service','Service']].map(([k,l])=>(
                <button key={k} onClick={()=>setGroupBy(k)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${groupBy===k?'bg-white text-[#2D1B4E] shadow-sm':'text-[#8E85A0]'}`} data-testid={`group-${k}`}>{l}</button>
              ))}
            </div>
          </div>
        )}
      </div>
      {isAdmin&&<Button onClick={()=>{setForm({title:'',level:'dsp',service_line:'',budgeted:1,filled:0,reports_to:'',description:''});setDlg('position');}} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2" data-testid="add-position-btn"><Plus className="h-4 w-4"/>Add Position</Button>}
    </div>

    {view==='tree'&&<Card className="border-[#DED8EB] shadow-sm" data-testid="org-hierarchy">
      <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#2D1B4E] flex items-center gap-2">
        <Network className="h-4 w-4 text-[#6B3FA0]"/>Org Hierarchy
        {isAdmin&&<span className="text-[10px] text-[#8E85A0] font-normal ml-1">Drag to re-parent · hover for inline actions</span>}
      </CardTitle></CardHeader>
      <CardContent>
        {hierarchy.length===0 ? (
          <p className="text-sm text-[#8E85A0] text-center py-4">Add positions to build the chart</p>
        ) : (
          <>
            {isAdmin && dragId && (
              <div
                onDragOver={e=>{e.preventDefault(); setRootHover(true);}}
                onDragLeave={()=>setRootHover(false)}
                onDrop={()=>{handleDrop(''); setRootHover(false);}}
                className={`mb-2 px-3 py-2 rounded-lg border-2 border-dashed text-xs text-center transition-all ${rootHover?'border-[#6B3FA0] bg-[#EDE7F6] text-[#2D1B4E] font-semibold':'border-[#DED8EB] text-[#8E85A0]'}`}
                data-testid="drop-to-root"
              >
                Drop here to make <strong>top level</strong> (no reports-to)
              </div>
            )}
            {groupBy === 'none' ? renderHier(hierarchy, 0, opts) : (
              groupedHierarchy && groupedHierarchy.length > 0 ? (
                <div className="space-y-4">
                  {groupedHierarchy.map(g => (
                    <div key={g.id} className="border border-[#DED8EB] rounded-lg overflow-hidden" data-testid={`group-section-${g.id}`}>
                      <div className="bg-[#F0EDF6] px-3 py-1.5 flex items-center gap-2 border-b border-[#DED8EB]">
                        <Building2 className="h-3.5 w-3.5 text-[#6B3FA0]"/>
                        <span className="text-xs font-bold text-[#2D1B4E] uppercase tracking-wider">{g.label}</span>
                        <span className="text-[10px] text-[#8E85A0] font-mono ml-auto">{g.tree.length} root{g.tree.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="p-2">{renderHier(g.tree, 0, opts)}</div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-[#8E85A0] text-center py-4">No positions assigned to any {groupBy === 'division' ? 'division' : 'service line'}. Edit positions to add.</p>
            )}
          </>
        )}
      </CardContent>
    </Card>}

    {view==='card'&&<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{positions.map(p=>{
      const isHovered = hoverId === p.id;
      const isDragging = dragId === p.id;
      return (
        <Card key={p.id}
          className={`border shadow-sm bg-white transition-all ${isHovered?'border-[#6B3FA0] ring-2 ring-[#6B3FA0]/20':isDragging?'border-[#9B7ED8] opacity-40':'border-[#DED8EB]'} ${isAdmin?'cursor-grab':''}`}
          draggable={isAdmin}
          onDragStart={()=>setDragId(p.id)}
          onDragEnd={()=>{setDragId(null); setHoverId(null);}}
          onDragOver={e=>{e.preventDefault(); setHoverId(p.id);}}
          onDragLeave={()=>setHoverId(null)}
          onDrop={()=>{handleDrop(p.id); setHoverId(null);}}
          data-testid={`pos-card-${p.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-sm font-bold text-[#2D1B4E]">{p.title}</h3>
                <Badge className={`text-[9px] mt-1 ${LC[p.level]||''}`}>{LL[p.level]}</Badge>
              </div>
              {isAdmin&&<div className="flex gap-0.5">
                <Button variant="ghost" size="icon" onClick={()=>onEdit(p)} className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`card-edit-${p.id}`}><Edit2 className="h-3 w-3"/></Button>
                <Button variant="ghost" size="icon" onClick={()=>del('position',p.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`card-delete-${p.id}`}><Trash2 className="h-3 w-3"/></Button>
              </div>}
            </div>
            <div className="flex items-center gap-2 text-xs text-[#5A4D6E]">
              <Users className="h-3 w-3 inline"/>{p.filled}/{p.budgeted}
              {p.service_line&&<Badge className="text-[9px] bg-[#F0EDF6] text-[#5A4D6E]">{SL[p.service_line]}</Badge>}
            </div>
            {p.filled<p.budgeted&&<p className="text-[10px] text-[#E65100] mt-1">{p.budgeted-p.filled} opening{p.budgeted-p.filled>1?'s':''}</p>}
          </CardContent>
        </Card>
      );
    })}</div>}

    {view==='table'&&<Card className="border-[#DED8EB] shadow-sm overflow-hidden"><CardContent className="p-0"><table className="w-full text-sm"><thead className="bg-[#F0EDF6]"><tr><th className="text-left px-4 py-2 text-xs font-semibold text-[#5A4D6E] uppercase">Title</th><th className="text-left px-4 py-2 text-xs font-semibold text-[#5A4D6E] uppercase">Level</th><th className="text-left px-4 py-2 text-xs font-semibold text-[#5A4D6E] uppercase">Service</th><th className="text-center px-4 py-2 text-xs font-semibold text-[#5A4D6E] uppercase">Filled</th><th className="text-center px-4 py-2 text-xs font-semibold text-[#5A4D6E] uppercase">Budget</th><th className="text-center px-4 py-2 text-xs font-semibold text-[#5A4D6E] uppercase">Gap</th><th className="px-4 py-2"></th></tr></thead><tbody>{positions.map(p=>{const gap=p.budgeted-p.filled;return(<tr key={p.id} className="border-t border-[#DED8EB] hover:bg-[#F7F4EF]"><td className="px-4 py-2.5 font-medium text-[#2D1B4E]">{p.title}</td><td className="px-4 py-2.5"><Badge className={`text-[9px] ${LC[p.level]||''}`}>{LL[p.level]}</Badge></td><td className="px-4 py-2.5 text-[#5A4D6E]">{SL[p.service_line]||'All'}</td><td className="px-4 py-2.5 text-center font-mono">{p.filled}</td><td className="px-4 py-2.5 text-center font-mono">{p.budgeted}</td><td className={`px-4 py-2.5 text-center font-mono font-bold ${gap>0?'text-[#E65100]':'text-[#2E7D32]'}`}>{gap>0?`+${gap}`:'-'}</td><td className="px-4 py-2.5 whitespace-nowrap">{isAdmin&&<><Button variant="ghost" size="icon" onClick={()=>onEdit(p)} className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`table-edit-${p.id}`}><Edit2 className="h-3 w-3"/></Button><Button variant="ghost" size="icon" onClick={()=>del('position',p.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`table-delete-${p.id}`}><Trash2 className="h-3 w-3"/></Button></>}</td></tr>);})}</tbody></table></CardContent></Card>}
  </div>);
}

function ProjectionsTab({projections, positions, config}){
  const byDivision = React.useMemo(() => {
    const divisions = config?.divisions || [];
    if (!divisions.length || !positions) return [];
    return divisions.map(d => {
      const divPositions = positions.filter(p => p.division_id === d.id);
      const budgeted = divPositions.reduce((a,p)=>a+(p.budgeted||0),0);
      const filled = divPositions.reduce((a,p)=>a+(p.filled||0),0);
      return { ...d, budgeted, filled, gap: budgeted - filled, count: divPositions.length };
    }).filter(d => d.count > 0);
  }, [config, positions]);

  return(<div className="mt-5 space-y-5">
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <Card className="border-[#DED8EB] shadow-sm"><CardContent className="p-5 text-center"><p className="text-3xl font-extrabold text-[#2D1B4E]">{projections.total_budgeted}</p><p className="text-xs text-[#8E85A0] uppercase mt-1">Budgeted</p></CardContent></Card>
      <Card className="border-[#DED8EB] shadow-sm"><CardContent className="p-5 text-center"><p className="text-3xl font-extrabold text-[#2E7D32]">{projections.total_filled}</p><p className="text-xs text-[#8E85A0] uppercase mt-1">Filled</p></CardContent></Card>
      <Card className="border-[#E65100] shadow-sm bg-[#FFF3E0]/30"><CardContent className="p-5 text-center"><p className="text-3xl font-extrabold text-[#E65100]">{projections.total_gap}</p><p className="text-xs text-[#8E85A0] uppercase mt-1">Hires Needed</p></CardContent></Card>
    </div>
    {byDivision.length > 0 && (
      <Card className="border-[#DED8EB] shadow-sm" data-testid="by-division-projections">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#2D1B4E] flex items-center gap-2"><Building2 className="h-4 w-4 text-[#6B3FA0]"/>By Division</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {byDivision.map(d => (
            <div key={d.id} className="p-3 rounded-lg border border-[#DED8EB] bg-white">
              <p className="text-sm font-bold text-[#2D1B4E]">{d.label}</p>
              <p className="text-[10px] text-[#8E85A0] mb-2">{d.count} positions</p>
              <div className="flex items-end justify-between">
                <span className="text-xs font-mono text-[#5A4D6E]">{d.filled}/{d.budgeted}</span>
                <span className={`text-sm font-bold ${d.gap>0?'text-[#E65100]':'text-[#2E7D32]'}`}>{d.gap>0?`+${d.gap}`:'✓ Staffed'}</span>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    )}
    {projections.gaps.length>0&&<Card className="border-[#DED8EB] shadow-sm"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#2D1B4E]">Open Gaps</CardTitle></CardHeader><CardContent className="space-y-2">{projections.gaps.map(g=><div key={g.position_id} className="flex items-center justify-between p-3 rounded-lg bg-[#FFF3E0]/50 border border-[#FFB74D]"><span className="text-sm font-semibold text-[#2D1B4E]">{g.title}</span><span className="text-sm font-bold text-[#E65100]">+{g.needed}</span></div>)}</CardContent></Card>}
  </div>);
}

function DescriptionsTab({jds,postings,isAdmin,setForm,setDlg,del,createPostingFromJd,config,api,fetchAll}){
  const postingsByJd = React.useMemo(() => {
    const m = {};
    (postings||[]).forEach(p=>{ if(p.job_description_id){ m[p.job_description_id]=(m[p.job_description_id]||0)+1; } });
    return m;
  }, [postings]);
  return(<div className="mt-5 space-y-5">
    <div className="flex items-center justify-between"><p className="text-sm text-[#5A4D6E]">{jds.length} descriptions</p>{isAdmin&&<Button onClick={()=>{setForm({title:'',summary:'',duties:'',qualifications:'',physical_reqs:'',benefits:'',salary_range:'',certifications:'',content:'',status:'draft',service_line:'',gen_title:'',gen_duties:'',gen_level:''});setDlg('jd');}} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2" data-testid="add-jd-btn"><Plus className="h-4 w-4"/>New JD</Button>}</div>
    <div className="space-y-3">{jds.map(jd=>{
      const postingCount = postingsByJd[jd.id]||0;
      return (
        <Card key={jd.id} className="border-[#DED8EB] shadow-sm hover:border-[#9B7ED8] transition-colors" data-testid={`jd-card-${jd.id}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0 cursor-pointer" onClick={()=>{if(isAdmin){setForm({...jd,gen_title:'',gen_duties:'',gen_level:''});setDlg('jd');}}}>
                <h3 className="text-sm font-bold text-[#2D1B4E]">{jd.title}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge className={`text-[9px] ${STATUS_STYLES[jd.status]||''}`}>{jd.status}</Badge>
                  {jd.employment_type && <Badge className={`text-[9px] border ${EMP_TYPE_STYLES[jd.employment_type]||''}`}>{EMP_TYPE_LABEL[jd.employment_type]}</Badge>}
                  {jd.salary_range&&<span className="text-[10px] text-[#5A4D6E]">{jd.salary_range}</span>}
                  {jd.service_line&&<Badge className="text-[9px] bg-[#F0EDF6] text-[#5A4D6E]">{SL[jd.service_line]}</Badge>}
                  {postingCount>0 ? (
                    <Badge className="text-[9px] bg-[#E8F5E9] text-[#1B5E20] gap-1"><Megaphone className="h-2.5 w-2.5"/>{postingCount} posting{postingCount>1?'s':''}</Badge>
                  ) : (
                    <Badge className="text-[9px] bg-gray-100 text-gray-500">No postings</Badge>
                  )}
                  {jd.summary&&<span className="text-[10px] text-[#8E85A0] truncate max-w-[220px]">· {jd.summary.replace(/\n/g,' ')}</span>}
                </div>
              </div>
              {isAdmin&&(
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" onClick={()=>createPostingFromJd(jd)} className="border-[#9B7ED8] text-[#6B3FA0] h-8 text-xs gap-1" data-testid={`post-from-jd-${jd.id}`}>
                    <Megaphone className="h-3 w-3"/>Post
                  </Button>
                  {postingCount>0 && (
                    <Button size="sm" variant="outline" onClick={async ()=>{
                      if (!window.confirm(`Push this JD's content into all ${postingCount} linked posting${postingCount>1?'s':''}? This overwrites their title, description, requirements, salary and service line.`)) return;
                      try {
                        const {data} = await api.post(`/staffing/job-descriptions/${jd.id}/sync-postings`);
                        toast.success(`Synced ${data.updated} posting${data.updated===1?'':'s'}`);
                        fetchAll();
                      } catch { toast.error('Sync failed'); }
                    }} className="border-[#81C784] text-[#2E7D32] h-8 text-xs gap-1" data-testid={`sync-jd-${jd.id}`}>
                      <Sparkles className="h-3 w-3"/>Sync {postingCount}
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={()=>{setForm({...jd,gen_title:'',gen_duties:'',gen_level:''});setDlg('jd');}} className="h-8 w-8 text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`edit-jd-${jd.id}`}><Edit2 className="h-3.5 w-3.5"/></Button>
                  <Button variant="ghost" size="icon" onClick={()=>del('jd',jd.id)} className="h-8 w-8 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`delete-jd-${jd.id}`}><Trash2 className="h-3.5 w-3.5"/></Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      );
    })}</div>
  </div>);
}

function PostingsTab({postings,jds,isAdmin,setForm,setDlg,del,openApps}){
  const [expanded, setExpanded] = React.useState(null);
  return(<div className="mt-5 space-y-5">
    <div className="flex items-center justify-between"><p className="text-sm text-[#5A4D6E]">{postings.filter(p=>p.status==='open'||p.is_active).length} live</p>{isAdmin&&<Button onClick={()=>{setForm({title:'',description:'',requirements:'',service_line:'',deadline:'',salary_range:'',job_description_id:'',status:'draft',is_public:false});setDlg('posting');}} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2" data-testid="add-posting-btn"><Plus className="h-4 w-4"/>New Posting</Button>}</div>
    <div className="space-y-3">{postings.map(p=>{
      const linkedJd = jds.find(j=>j.id===p.job_description_id);
      const isOpen = expanded === p.id;
      return (
        <Card key={p.id} className={`border shadow-sm transition-all ${p.is_active?'border-[#DED8EB]':'border-gray-200 opacity-70'} ${isOpen?'ring-2 ring-[#9B7ED8]/30':''}`} data-testid={`posting-card-${p.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-bold text-[#2D1B4E]">{p.title}</h3>
                  <Badge className={`text-[9px] ${STATUS_STYLES[p.status]||STATUS_STYLES.draft}`}>{p.status}</Badge>
                  {p.employment_type && <Badge className={`text-[9px] border ${EMP_TYPE_STYLES[p.employment_type]||''}`}>{EMP_TYPE_LABEL[p.employment_type]}</Badge>}
                  {p.is_public && <Badge className="text-[9px] bg-[#E0F2F1] text-[#00695C] border border-[#80CBC4] gap-1"><Globe className="h-2.5 w-2.5"/>Public</Badge>}
                  {linkedJd && (
                    <Badge className="text-[9px] bg-[#EDE7F6] text-[#6B3FA0] gap-1"><FileText className="h-2.5 w-2.5"/>Linked JD</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap text-[11px] text-[#5A4D6E]">
                  {p.service_line&&<Badge className="text-[9px] bg-[#F0EDF6] text-[#5A4D6E]">{SL[p.service_line]}</Badge>}
                  {p.salary_range&&<span>{p.salary_range}</span>}
                  {p.deadline&&<span className="text-[#8E85A0]"><Clock className="h-3 w-3 inline mr-0.5"/>{p.deadline}</span>}
                </div>
                {p.description && !isOpen && <p className="text-xs text-[#5A4D6E] mt-1 line-clamp-2">{p.description}</p>}
              </div>
              <div className="flex gap-1 shrink-0 ml-2">
                <Button variant="outline" size="sm" onClick={()=>setExpanded(isOpen?null:p.id)} className="border-[#DED8EB] text-[#6B3FA0] h-7 text-xs gap-1" data-testid={`toggle-posting-${p.id}`}>
                  {isOpen ? <>Collapse</> : <>View</>}
                </Button>
                <Button variant="outline" size="sm" onClick={()=>openApps(p)} className="border-[#DED8EB] text-[#6B3FA0] h-7 text-xs gap-1" data-testid={`posting-apps-${p.id}`}><Users className="h-3 w-3"/>Apps</Button>
                {isAdmin&&<Button variant="ghost" size="icon" onClick={()=>{setForm({...p});setDlg('posting');}} className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`edit-posting-${p.id}`}><Edit2 className="h-3 w-3"/></Button>}
                {isAdmin&&<Button variant="ghost" size="icon" onClick={()=>del('posting',p.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`delete-posting-${p.id}`}><Trash2 className="h-3 w-3"/></Button>}
              </div>
            </div>

            {isOpen && (
              <div className="mt-3 pt-3 border-t border-[#DED8EB] space-y-3">
                {p.description && <Section label="Description" body={p.description}/>}
                {p.requirements && <Section label="Requirements" body={p.requirements}/>}
                {linkedJd && (
                  <div className="bg-[#F7F4EF] border border-[#DED8EB] rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-[#6B3FA0]"/>
                      <span className="text-xs font-semibold text-[#2D1B4E]">From linked JD: {linkedJd.title}</span>
                    </div>
                    {linkedJd.duties && <Section label="Essential Duties" body={linkedJd.duties}/>}
                    {linkedJd.qualifications && <Section label="Qualifications" body={linkedJd.qualifications}/>}
                    {linkedJd.physical_reqs && <Section label="Physical Requirements" body={linkedJd.physical_reqs}/>}
                    {linkedJd.benefits && <Section label="Benefits" body={linkedJd.benefits}/>}
                    {linkedJd.certifications && <Section label="Certifications" body={linkedJd.certifications}/>}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      );
    })}</div>
  </div>);
}


